import React, { useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import {
  formatDateTime, formatMW, useContainerWidth, EmptyState,
  CHART_MARGINS, defaultLineLegendProps, tickLabelStyle, rotatedTickLabelStyle,
  toggleButtonGroupSx,
} from './shared';

/**
 * Forecast Accuracy Chart (Fan Chart)
 *
 * Compares UIGF forecast (with POE10/50/90 confidence bands) against
 * actual dispatch for solar/wind assets. Quantifies forecast risk and
 * shows whether assets are systematically over/under-forecasted.
 *
 * Data available from Aug 2024+.
 */
const ForecastAccuracyChart = ({ data, assets, frequency }) => {
  const { ref, width } = useContainerWidth();
  const [selectedAsset, setSelectedAsset] = useState(null);

  if (!data || data.length === 0) {
    return <EmptyState message="No forecast data available (requires Solar/Wind assets, data from Aug 2024+)" />;
  }

  const assetIds = [...new Set(data.map((d) => d.asset_id))];
  const assetLookup = {};
  (assets || []).forEach((a) => { assetLookup[a.asset_id] = a; });

  const activeAsset = selectedAsset || assetIds[0];
  const assetData = data.filter((d) => d.asset_id === activeAsset);
  const sortedData = [...assetData].sort((a, b) => String(a.date_time).localeCompare(String(b.date_time)));

  const xLabels = sortedData.map((d) => formatDateTime(d.date_time, frequency));

  const series = [
    {
      type: 'line',
      label: 'POE90 (Low)',
      data: sortedData.map((d) => d.forecast_poe90),
      color: '#bfdbfe',
      curve: 'monotone',
      area: true,
    },
    {
      type: 'line',
      label: 'UIGF (Forecast)',
      data: sortedData.map((d) => d.uigf),
      color: '#3b82f6',
      curve: 'monotone',
    },
    {
      type: 'line',
      label: 'POE10 (High)',
      data: sortedData.map((d) => d.forecast_poe10),
      color: '#93c5fd',
      curve: 'monotone',
      area: true,
    },
    {
      type: 'line',
      label: 'Actual',
      data: sortedData.map((d) => d.actual_mw),
      color: '#ef4444',
      curve: 'monotone',
    },
  ];

  return (
    <Box>
      {assetIds.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            value={activeAsset}
            exclusive
            onChange={(_, v) => { if (v) setSelectedAsset(v); }}
            size="small"
            sx={{ ...toggleButtonGroupSx, flexWrap: 'wrap' }}
          >
            {assetIds.map((aid) => (
              <ToggleButton key={aid} value={aid}>{assetLookup[aid]?.name || aid}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      <Box ref={ref} sx={{ width: '100%', height: 420, minWidth: 0, overflow: 'hidden' }}>
        {width > 0 && (
          <LineChart
            width={width}
            height={420}
            series={series}
            xAxis={[{
              scaleType: 'point',
              data: xLabels,
              tickLabelStyle: rotatedTickLabelStyle,
              tickNumber: xLabels.length > 30 ? 30 : undefined,
            }]}
            yAxis={[{ valueFormatter: formatMW, tickLabelStyle }]}
            margin={CHART_MARGINS}
            slotProps={{ legend: { ...defaultLineLegendProps } }}
          />
        )}
      </Box>
    </Box>
  );
};

export default ForecastAccuracyChart;
